import { Link } from "wouter";
import { AlertTriangle } from "lucide-react";
import { useChannels } from "@client/hooks";

/**
 * A disconnected WhatsApp, said where it is noticed.
 *
 * It was visible in two places, neither of them where you work: a `canal off` badge on each row
 * of the conversation list, and the Configurações card. Hoje's "Conecte seu WhatsApp" fires only
 * when there are **zero** channels, so a number that had dropped looked exactly like a quiet day.
 * You found out by writing a reply and watching it fail.
 *
 * It links rather than reconnects. Connecting is `owner`/`manager` only — an atendente would get
 * a 403 from a button that looked available — and a channel WhatsApp logged out of needs the QR
 * drawer, which lives in Configurações. One honest link beats a button that works for one role
 * and one of the two failure modes.
 *
 * `useChannels` already polls every ten seconds while anything is disconnected, so this clears
 * itself without being told.
 */
export function ChannelDownBanner() {
  const { data } = useChannels();

  // "disabled" is off on purpose, not broken; saying so would be nagging about a choice.
  const down = (data ?? []).filter((c) => c.status !== "connected" && c.status !== "disabled");
  if (down.length === 0) return null;

  const one = down.length === 1 ? down[0]! : null;
  const awaiting = down.every((c) => c.status === "awaiting_qr");

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-[color:var(--tint-warning-fg)]/15
                 bg-tint-warning-bg px-4 py-2.5 text-xs text-tint-warning-fg md:px-6"
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white/70">
        <AlertTriangle className="h-3.5 w-3.5 text-tint-warning-fg" strokeWidth={2} />
      </span>
      <span className="font-medium text-foreground">
        {awaiting
          ? one ? `${one.name} está esperando a leitura do código`
            : `${down.length} canais estão esperando a leitura do código`
          : one ? `${one.name} está desconectado`
            : `${down.length} canais estão desconectados`}
      </span>
      <span className="text-muted-foreground">
        Suas mensagens não vão sair enquanto isso.
      </span>
      <Link href="/app/configuracoes/canais"
            className="ml-auto rounded-full bg-white px-3 py-1 font-semibold text-tint-warning-fg shadow-[0_1px_2px_rgb(15_27_36/0.06)] hover:bg-white/80">
        Reconectar
      </Link>
    </div>
  );
}
